/**
 * Derive the owning module of a React Query cache entry from its query key.
 *
 * This is what makes the freshness layer **zero opt-in**: a page never enrols
 * itself, the provider reads the module straight off the keys already in the
 * cache. Two key shapes exist in the app:
 *
 * 1. **Orval-generated keys** — `['/api/v1/pipeline/leads', params]`. The first
 *    element is the API path, so the module falls out of the first path segment
 *    (`pipeline`), kebab-cased. Where the route prefix and the module id differ
 *    (`/api/v1/admin/*` is owned by `user_management`), `API_PREFIX_MODULES`
 *    carries the difference. This covers every generated hook automatically —
 *    new endpoints need no change here.
 * 2. **Hand-rolled keys** — `['deals']`, `activityKeys.byDeal(id)`. These carry
 *    no path, so they need the explicit `HAND_ROLLED_KEY_MODULES` map below.
 *
 * A key that resolves to `null` simply gets no live refresh — the pre-existing
 * behaviour. Nothing breaks; it just doesn't improve.
 *
 * The module ids are the **keys** of `modules_entities.config.json` `modules`,
 * because that is what the backend cue reports
 * (`shared/data_freshness/module_map.py`). Deliberately NOT `ModuleConfig.id`,
 * which is a display-level identifier that diverges for five modules
 * (`user_management` → `admin`, `ai_agents` → `ai`, …) and would silently match
 * nothing.
 */

import type { QueryKey } from '@tanstack/react-query';
import modulesEntitiesConfig from '../../shared-config/modules_entities.config.json';

/** Every module id the backend can name in a cue. */
export const CUE_MODULE_IDS: ReadonlySet<string> = new Set(
  Object.keys((modulesEntitiesConfig as { modules: Record<string, unknown> }).modules),
);

/**
 * Sentinel for query keys whose data is **cross-module by construction** — the
 * activity feed renders rows produced by writes in every module, so no single
 * module id is correct for it.
 *
 * A key resolving to this is invalidated by a cue from *any* module, and is
 * never itself turned into a WebSocket subscription (there is no `*` channel;
 * the page's other queries are what subscribe it to the modules it cares about).
 * Use it sparingly: it is deliberately a widening of the invalidation predicate,
 * not a shortcut for "I could not work out the module".
 */
export const CROSS_MODULE = '*';

/**
 * API path prefix → module id, for prefixes whose kebab→snake form is not
 * already a module id.
 *
 * Derived from the backend routers (`prefix="/api/v1/..."` under
 * `modules/{module}/api/` and `shared/`). A prefix mapped to `null` is
 * infrastructure or cross-cutting plumbing that no module's cue covers
 * (`/api/v1/auth`, `/api/v1/search`, `/api/v1/health`): mapping it explicitly
 * documents that the omission is deliberate rather than forgotten.
 */
export const API_PREFIX_MODULES: Readonly<Record<string, string | null>> = {
  // user_management (route prefix `admin`, plus the auth-adjacent surfaces).
  // `companies` here is the tenant's own legal entities (`user_management_router`
  // is mounted at a bare `/api/v1`) — NOT the CRM companies of the same name in
  // `HAND_ROLLED_KEY_MODULES`, which are master_data contacts.
  admin: 'user_management',
  auth: null,
  companies: 'user_management',
  groups: 'user_management',
  invitations: 'user_management',
  onboarding: 'user_management',
  'setup-wizard': 'user_management',
  users: 'user_management',
  // shared/trade_exchange — the intercompany document-pair read seam; the
  // documents it describes are sales/procurement/accounting's, the panel is
  // accounting-owned (WI 2ed3e761).
  'trade-exchange': 'accounting',
  // file_management
  'document-requests': 'file_management',
  'document-types': 'file_management',
  'external-storage': 'file_management',
  files: 'file_management',
  folders: 'file_management',
  // shipments (logistics sub-surfaces)
  cfs: 'shipments',
  consols: 'shipments',
  customs: 'shipments',
  forwarders: 'shipments',
  'freight-settlements': 'shipments',
  // One document by its own id — global search's result target. Its own prefix
  // because a literal `documents` segment under `/api/v1/shipments` would be
  // swallowed by that router's `/{shipment_id}` catch-all.
  'shipment-documents': 'shipments',
  'shipment-estimates': 'shipments',
  // Cross-cutting saved list-page views: ONE prefix serving surfaces registered
  // by several modules, so it has no owning module. `null` rather than
  // CROSS_MODULE because a saved view only ever changes when its own user edits
  // it — the mutation hooks invalidate it directly, and no other module's cue
  // says anything about it. (It was 'shipments' while the table was
  // shp_saved_views, which re-fetched the list on every unrelated shipments cue.)
  'saved-views': null,
  // ai_agents (a prefix that does not kebab→snake into the module id)
  'agent-runs': 'ai_agents',
  // third_party_services
  ai: 'third_party_services',
  integrations: 'third_party_services',
  logistics: 'third_party_services',
  // website_hosting
  websites: 'website_hosting',
  'website-plugins': 'website_hosting',
  'website-templates': 'website_hosting',
  'website-wizard': 'website_hosting',
  // shared (cross-cutting entities that do own tables and do emit cues)
  activity: 'shared',
  comments: 'shared',
  // The comms channel registry (shared/comms) — every channel module registers
  // into one surface, so no single module id is right; owned by shared/.
  comms: 'shared',
  connectors: 'shared',
  'custom-fields': 'shared',
  'document-designs': 'shared',
  'document-distribution': 'shared',
  'document-line-layouts': 'shared',
  'document-numbering': 'shared',
  'email-suppression': 'shared',
  // Backlink panels are cross-module by construction: one counts response carries
  // every contributing module's badge, so no single module id is correct for the
  // key. A cue from any of them should refresh it — a new deal changes the
  // contact page's Deals count, and a new ticket changes its Tickets count.
  'entity-links': CROSS_MODULE,
  'entity-sync': 'shared',
  follows: 'shared',
  ingestion: 'shared',
  'integration-connections': 'shared',
  'manager-hierarchy': 'shared',
  'notification-preferences': 'shared',
  notifications: 'shared',
  permissions: 'shared',
  'platform-billing': 'shared',
  preferences: 'shared',
  push: 'shared',
  reminders: 'shared',
  settings: 'shared',
  'sync-runs': 'shared',
  tags: 'shared',
  tenant: 'shared',
  tenants: 'shared',
  usage: 'shared',
  versions: 'shared',
  // other modules whose route prefix differs from their id
  careers: 'recruiting',
  dashboard: 'home',
  emails: 'email',
  forms: 'tasks',
  mediation: 'messaging',
  workspaces: 'tasks',
  // Infrastructure / multi-owner prefixes — no single module owns them, so a
  // cue could never be scoped correctly. Explicitly not covered.
  a2a: null,
  'ai-intake': null,
  'audit-logs': null,
  debug: null,
  'demo-data': null,
  external: null,
  health: null,
  internal: null,
  mcp: null,
  monitoring: null,
  // Cross-tenant by construction, and the reason is not cosmetic. My
  // Correspondence reads tickets and invoices that OTHER tenants addressed to my
  // verified email; the cue channel is `changes:{my_tenant}:{module}`, so no cue
  // this client can ever receive reports a change to that data. It used to be
  // mapped to `user_management` — the module that owns the ROUTE — which made
  // every `users`/`groups`/`companies` write in my own tenant refetch it, on
  // every open tab, past its 5-minute `staleTime` (`invalidateQueries` ignores
  // staleTime). Its endpoint shares the cross-tenant correspondent-read limiter
  // (30/min per USER, counted across all tabs), so on a busy tenant the
  // user-menu probe in `UserMenu.tsx` — mounted on EVERY page — 429'd and toasted
  // "Rate limit exceeded" at users idling on unrelated pages (prod
  // 2026-08-24 07:53–08:44 UTC, errorIds d3365718/c3901f69/…).
  'my-correspondence': null,
  public: null,
  search: null,
  status: null,
  // Cross-tenant console. `sysadmin` IS a config module key, so without this it
  // would auto-resolve — inert today (its one table has no tenant_id, so the
  // collector never publishes a cue) but wrong in principle: the cue channel is
  // keyed by the viewer's own tenant and cannot carry cross-tenant data.
  sysadmin: null,
  webhooks: null,
  'whatsapp-channel': null,
  ws: null,
};

/**
 * First element of a hand-rolled query key → module id.
 *
 * Unlike the generated keys these cannot be derived, so every entry is manual.
 * A new hand-rolled key factory that lands unmapped is invisible here by
 * construction — `listUnmappedHandRolledKeys()` below exposes the gap, and the
 * rollout work item owns the meta-test that fails the build on it.
 */
export const HAND_ROLLED_KEY_MODULES: Readonly<Record<string, string | null>> = {
  // pipeline
  activities: 'pipeline',
  'contact-enrollments': 'pipeline',
  'deal-inventory': 'pipeline',
  dealBoard: 'pipeline',
  dealProducts: 'pipeline',
  deals: 'pipeline',
  'email-templates': 'pipeline',
  enrollments: 'pipeline',
  'lead-milestone-criteria': 'pipeline',
  'lead-milestones': 'pipeline',
  'lead-source-agent-library': 'pipeline',
  'lead-source-agents': 'pipeline',
  leads: 'pipeline',
  'outreach-tasks': 'pipeline',
  pipeline: 'pipeline',
  pipelines: 'pipeline',
  'prospect-research': 'pipeline',
  'prospecting-analytics': 'pipeline',
  'sales-dashboard': 'pipeline',
  'sales-reports': 'pipeline',
  salesSettings: 'pipeline',
  'sequence-steps': 'pipeline',
  sequences: 'pipeline',
  'stage-criteria': 'pipeline',
  'step-variants': 'pipeline',
  // master_data
  addons: 'master_data',
  backlinks: 'master_data',
  bundles: 'master_data',
  companies: 'master_data',
  'contact-merge-preview': 'master_data',
  contacts: 'master_data',
  // Its own head rather than a `['contacts', …]` sub-key: the bulk-relationship
  // apply invalidates the `contacts` prefix, and prefix-matching this coverage
  // query would refetch it for a dialog that is closing.
  'contacts-bulk-relationship-summary': 'master_data',
  'contacts-companies': 'master_data',
  'contacts-tiers': 'master_data',
  'hs-codes': 'master_data',
  // Tenant-defined interaction types (WI 1d51bfd0) — read on the contact
  // timeline, so it lives in the same module as `contacts`.
  'interaction-types': 'master_data',
  'md-addresses': 'master_data',
  'md-location-picker': 'master_data',
  'md-location-references': 'master_data',
  'organization-merge-preview': 'master_data',
  organizations: 'master_data',
  'portal-products': 'master_data',
  'price-lists': 'master_data',
  'pricing-campaigns': 'master_data',
  'pricing-rules': 'master_data',
  'product-categories': 'master_data',
  'product-category-coverage': 'master_data',
  'product-packagings': 'master_data',
  'product-price': 'master_data',
  'product-references': 'master_data',
  'product-tax-categories': 'master_data',
  'product-vendor-skus': 'master_data',
  'product-warehouse-accounts': 'master_data',
  productDeals: 'master_data',
  productPriceListEntries: 'master_data',
  products: 'master_data',
  'products-settings': 'master_data',
  'relationship-classes': 'master_data',
  'warehouse-geocode': 'master_data',
  whatsapp: 'master_data',
  // The Integration Hub's WhatsApp registry (`wa_connections`): written by
  // whatsapp_channel's connect flows, re-read after marketing's capture toggle.
  'whatsapp-connections': CROSS_MODULE,
  // inventory
  // The bare head every inventory read shares, used as an invalidation prefix
  // when title transfer creates stock (`procurement/hooks/useInboundDeliveries`).
  inventory: 'inventory',
  'inventory-dashboard': 'inventory',
  'inventory-posting-job': 'inventory',
  'inventory-reports': 'inventory',
  'inventory-settings': 'inventory',
  'product-inventory-settings': 'inventory',
  'reorder-rules': 'inventory',
  'stock-adjustments': 'inventory',
  'stock-inquiry': 'inventory',
  'stock-levels': 'inventory',
  'stock-lots': 'inventory',
  'stock-movement-wizard': 'inventory',
  'stock-movements': 'inventory',
  'stock-take-history': 'inventory',
  'stock-takes': 'inventory',
  'stock-transfers': 'inventory',
  warehouses: 'inventory',
  // accounting
  accounting: 'accounting',
  'accounting-company-gl-mappings': 'accounting',
  'accounting-gl-mappings-coverage': 'accounting',
  'accounting-customer-credit': 'accounting',
  'accounting-payment-gateway': 'accounting',
  'accounting-settings': 'accounting',
  'ach-return-codes': 'accounting',
  'bank-reconciliation': 'accounting',
  'bank-rules': 'accounting',
  'bank-transactions': 'accounting',
  // ACC-J4 reconciliation session: the band's figures are derived from the bank
  // feed, so they must live-refresh on the same accounting cues the grid does.
  'reconciliation-session': 'accounting',
  'reconciliation-session-history': 'accounting',
  'reconciliation-summary': 'accounting',
  'deferred-revenue-rollforward': 'accounting',
  'invoice-revenue-recognition-schedule': 'accounting',
  invoices: 'accounting',
  'morning-invoice-doc': 'accounting',
  'morning-payment-doc': 'accounting',
  payments: 'accounting',
  'payments-due': 'accounting',
  'tax-rates': 'accounting',
  // file_management — `FILES_QUERY_KEY`, the head every file/attachment query
  // in `shared/files/hooks/useFiles.ts` carries.
  files: 'file_management',
  // procurement
  procurement: 'procurement',
  'procurement-dashboard': 'procurement',
  'procurement-goods-receipt-attachments': 'procurement',
  'procurement-goods-receipts': 'procurement',
  'procurement-landed-cost': 'procurement',
  'procurement-license-plates': 'procurement',
  'procurement-po-drop-ship': 'procurement',
  'procurement-po-shipments': 'procurement',
  'procurement-purchase-orders': 'procurement',
  'procurement-receipt-reason-codes': 'procurement',
  'procurement-recurring-po-templates': 'procurement',
  'procurement-reports': 'procurement',
  'procurement-requisitions': 'procurement',
  'procurement-rfqs': 'procurement',
  'procurement-settings': 'procurement',
  'procurement-supplier-claims': 'procurement',
  'procurement-supplier-pis': 'procurement',
  'procurement-three-way-match': 'procurement',
  'procurement-vendor-returns': 'procurement',
  'procurement-vendors': 'procurement',
  'vendor-product-prices': 'procurement',
  // sales
  sales: 'sales',
  // Its own head rather than a `['sales', 'partners', …]` sub-key: the
  // "Change channels" apply invalidates the partner prefix, and prefix-matching
  // this coverage query would refetch it for a dialog that is closing — on the
  // escalated path a second full filter-set resolution, taken under the route's
  // per-tenant concurrency cap, for a result nobody reads.
  'sales-partner-channel-coverage': 'sales',
  'sales-inbound-allocation': 'sales',
  // Cross-cutting saved list-page views — see the API_PREFIX_MODULES row above
  // for why this is `null` rather than a module id or CROSS_MODULE.
  'saved-views': null,
  // shipments
  consols: 'shipments',
  customs: 'shipments',
  forwarders: 'shipments',
  'freight-settlements': 'shipments',
  'shipment-estimates': 'shipments',
  cfs: 'shipments',
  compliance: 'shipments',
  shipments: 'shipments',
  'shipments-dashboard': 'shipments',
  'shipments-settings': 'shipments',
  // contracts
  contracts: 'contracts',
  // support
  kb: 'support',
  'portal-notifications': 'support',
  'portal-tickets': 'support',
  support: 'support',
  'support-agents': 'support',
  'support-routing-rules': 'support',
  'support-sla-policies': 'support',
  'support-widget': 'support',
  supportSettings: 'support',
  tickets: 'support',
  // recruiting
  applications: 'recruiting',
  'ats-agents': 'recruiting',
  'ats-departments': 'recruiting',
  'ats-job-agencies': 'recruiting',
  'ats-pipelines': 'recruiting',
  'ats-source-stats': 'recruiting',
  candidates: 'recruiting',
  interviews: 'recruiting',
  job: 'recruiting',
  'job-stage-questions': 'recruiting',
  jobBoard: 'recruiting',
  jobInterviewers: 'recruiting',
  jobs: 'recruiting',
  'planned-questions': 'recruiting',
  // Recruiter PORTAL (`external/pages/`). These name recruiting entities, but the
  // reader is an external user reaching them through the portal, and a cue is
  // published on the internal app's channel for the tenant's own staff — so
  // resolving these to `recruiting` would not make the portal refresh, it would
  // only claim it does. `null` is the recorded decision: no live refresh.
  'recruiter-jobs': null,
  'recruiter-submissions': null,
  recruiting: 'recruiting',
  'recruiting-ai': 'recruiting',
  'recruiting-calendar': 'recruiting',
  'recruiting-candidate-sourcing': 'recruiting',
  'recruiting-offers': 'recruiting',
  'recruiting-reports': 'recruiting',
  rejectionReasons: 'recruiting',
  scorecardTemplates: 'recruiting',
  // tasks
  automations: 'tasks',
  boards: 'tasks',
  form: 'tasks',
  'form-submissions': 'tasks',
  forms: 'tasks',
  item: 'tasks',
  'item-occurrences': 'tasks',
  items: 'tasks',
  'public-form': 'tasks',
  sprints: 'tasks',
  tasks: 'tasks',
  'tasks-dashboard': 'tasks',
  // Board templates (`tasks_board_templates`) — tasks data despite the generic
  // key names. All five come from modules/tasks/hooks/useTemplates.ts.
  template: 'tasks',
  'template-categories': 'tasks',
  'template-gallery': 'tasks',
  'template-recommendations': 'tasks',
  templates: 'tasks',
  workspaces: 'tasks',
  workTracking: 'tasks',
  // forms_bpm. `publicForm` is the public form *renderer* page — not to be
  // confused with `public-form` above, the tasks module's own public form hook.
  'forms-bpm': 'forms_bpm',
  publicForm: 'forms_bpm',
  // software_factory
  sdevChangelog: 'software_factory',
  sdevDashboard: 'software_factory',
  sdevExceptions: 'software_factory',
  sdevInitiatives: 'software_factory',
  sdevMilestones: 'software_factory',
  sdevModules: 'software_factory',
  sdevPortfolio: 'software_factory',
  sdevShippingReport: 'software_factory',
  sdevSprints: 'software_factory',
  sdevWorkItemCustomerCoverage: 'software_factory',
  sdevWorkItems: 'software_factory',
  sfAnnouncements: 'software_factory',
  'software-factory': 'software_factory',
  // ai_agents
  'accessible-virtual-users': 'ai_agents',
  'agent-chats': 'ai_agents',
  'agent-mcp-servers': 'ai_agents',
  'agent-runs': 'ai_agents',
  'agent-shared-data': 'ai_agents',
  'agent-stack': 'ai_agents',
  'ai-agents': 'ai_agents',
  'ai-agents-agent': 'ai_agents',
  'ai-agents-avatars': 'ai_agents',
  'ai-agents-dashboard': 'ai_agents',
  'ai-agents-voices': 'ai_agents',
  'all-virtual-users': 'ai_agents',
  memory: 'ai_agents',
  octopus: 'ai_agents',
  // ai_chat
  'ai-chat': 'ai_chat',
  chat: 'ai_chat',
  'chat-tool-call': 'ai_chat',
  'chat-user-settings': 'ai_chat',
  // avatars
  'avatar-lab': 'avatars',
  avatarFaces: 'avatars',
  avatars: 'avatars',
  // messaging
  // `mediation` is the head of `shared/mediation`'s key factory (thread view,
  // triage queue, per-lane line options). It spells the head through a constant
  // (`MEDIATION_KEY`) rather than a literal, so the coverage scan — which reads
  // literal arrays — never saw it and the whole mediated-correspondence surface
  // resolved to `null`, i.e. never live-refreshed.
  mediation: 'messaging',
  messaging: 'messaging',
  // marketing
  'channel-connection': 'marketing',
  'channel-connections': 'marketing',
  'channel-export-job': 'marketing',
  'channel-export-preview-job': 'marketing',
  'channel-locations': 'marketing',
  'channel-mappings': 'marketing',
  'channel-seed-readiness': 'marketing',
  'channel-sync-runs': 'marketing',
  'channel-unmatched-tax-rates': 'marketing',
  marketing: 'marketing',
  'shopify-install-link': 'marketing',
  // billing
  billing: 'billing',
  // brokerage
  brokerage: 'brokerage',
  // website_hosting
  websites: 'website_hosting',
  'website-plugins': 'website_hosting',
  'website-templates': 'website_hosting',
  'website-wizard': 'website_hosting',
  // third_party_services
  'google-integration': 'third_party_services',
  'integration-connections': 'third_party_services',
  'microsoft-integration': 'third_party_services',
  notion: 'third_party_services',
  // user_management
  admin: 'user_management',
  'admin-activity-log': 'user_management',
  'can-invite': 'user_management',
  groups: 'user_management',
  'invitation-verify': 'user_management',
  'tenant-locale-settings': 'user_management',
  users: 'user_management',
  // shared
  'data-import-design': 'shared',
  'custom-fields': 'shared',
  'document-designs': 'shared',
  'manager-hierarchy': 'shared',
  'notification-preferences': 'shared',
  versions: 'shared',
  'data-import-history': 'shared',
  // Module-agnostic: shared/documents/hooks/useDocumentSendDefaults.ts, backed
  // by shared/api/document_distribution_routes.py and used from the shared
  // SendDocumentDialog across invoices, quotes, sales orders and contracts.
  documentSendDefaults: 'shared',
  'entity-tags-batch': 'shared',
  follows: 'shared',
  'ingestion-batch': 'shared',
  'notification-settings': 'shared',
  'notifications-inbox': 'shared',
  permissions: 'shared',
  'platform-bill': 'shared',
  'reminder-rules': 'shared',
  'reminder-settings': 'shared',
  settings: 'shared',
  'sync-run-items': 'shared',
  'tag-detail': 'shared',
  // Cross-module by construction — the activity feed and timeline render rows
  // produced by writes in every module, so any cue is relevant to them.
  'activity-events': CROSS_MODULE,
  // Explicitly NOT covered. Three reasons, all deliberate:
  //   * `portal-*` / `agent-portal-*` / `vendor-*` / `externalForm*` /
  //     `freight-quote-approval` — the external portal, whose users hold no
  //     internal notification socket, so there is no cue channel to ride;
  //   * `sysadmin-*` / `tenant-*` / `mcp-*` / `ai-model-*` / `system-health` —
  //     cross-tenant consoles; the cue channel is keyed by the viewer's own
  //     tenant and cannot carry another tenant's data;
  //   * `global-search` — a query over the search index, not a module's tables.
  // Mapping them explicitly is what keeps the guard below honest: the omission
  // is recorded, not forgotten.
  'agent-portal-access': null,
  'agent-portal-leg': null,
  'agent-portal-legs': null,
  'agent-portal-request': null,
  'agent-portal-requests': null,
  'ai-capabilities': null,
  'ai-model-metadata': null,
  'chat-model-categories': null,
  externalForm: null,
  externalForms: null,
  'freight-quote-approval': null,
  'global-search': null,
  'mcp-module-tools': null,
  'mcp-modules': null,
  'mcp-servers': null,
  'portal-cart': null,
  'portal-catalog': null,
  'portal-kb': null,
  'portal-mediation-sides': null,
  'portal-order': null,
  'portal-order-installments': null,
  'portal-order-journey': null,
  'portal-orders': null,
  'portal-product': null,
  'portal-ship-to-addresses': null,
  'portal-shipment': null,
  'portal-shipment-documents': null,
  'portal-shipment-invoices': null,
  'portal-shipments': null,
  sysadmin: null,
  'sysadmin-ai-markup': null,
  'sysadmin-alerts': null,
  'sysadmin-tenant-top-users': null,
  'sysadmin-usage-detailed': null,
  'system-health': null,
  'tenant-picker-search': null,
  'tenant-pricing': null,
  'tenant-usage-detailed': null,
  'usage-top-users': null,
  'vendor-mediation-sides': null,
  'vendor-purchase-order': null,
  'vendor-purchase-orders': null,
  'vendor-rfq': null,
  'vendor-rfqs': null,
};

const API_PATH_PREFIX = '/api/';

/** Extract the first path segment after `/api/` or `/api/v1/`. */
function apiPrefixOf(path: string): string | null {
  const segments = path.slice(API_PATH_PREFIX.length).split('/').filter(Boolean);
  if (segments.length === 0) return null;
  // Version segments (`v1`, `v2`) sit between `/api/` and the route prefix.
  const first = /^v\d+$/.test(segments[0]) ? segments[1] : segments[0];
  return first ?? null;
}

/**
 * The module that owns the data behind a query key, or `null` when unknown.
 *
 * Never throws — it runs on every entry in a cache that can hold hundreds of
 * queries, and a malformed key must not take the freshness layer down.
 */
export function moduleFromQueryKey(queryKey: QueryKey): string | null {
  const head = Array.isArray(queryKey) ? queryKey[0] : queryKey;
  if (typeof head !== 'string' || head.length === 0) return null;

  if (head.startsWith(API_PATH_PREFIX)) {
    const prefix = apiPrefixOf(head);
    if (!prefix) return null;
    if (prefix in API_PREFIX_MODULES) return API_PREFIX_MODULES[prefix];
    const candidate = prefix.replace(/-/g, '_');
    return CUE_MODULE_IDS.has(candidate) ? candidate : null;
  }

  if (head in HAND_ROLLED_KEY_MODULES) return HAND_ROLLED_KEY_MODULES[head];
  return null;
}

/**
 * Hand-rolled key heads present in `keys` that carry no mapping.
 *
 * Exposed so the rollout item's meta-test can list — and fail on — a new key
 * factory that nobody mapped. Returned sorted so the failure message is stable.
 */
export function listUnmappedHandRolledKeys(keys: readonly string[]): string[] {
  return [...new Set(keys.filter((k) => !k.startsWith(API_PATH_PREFIX) && !(k in HAND_ROLLED_KEY_MODULES)))].sort();
}
